import { useEffect, useMemo, useState } from "react";

import {
  type TaxonomySupportedCategory,
  useTaxonomySupportedCategoriesQuery,
  useTaxonomySupportedMarketplacesQuery,
} from "@/graphql/generated";

import type {
  CategoryCoverageRow,
  CompareMarketplaceOption,
  CompareMarketplaceSummary,
} from "./useCompareMarketplaces.types";

export type {
  CategoryCoverageRow,
  CompareMarketplaceOption,
  CompareMarketplaceSummary,
} from "./useCompareMarketplaces.types";

function toMarketplaceName(code: string): string {
  return code
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatCategoryPath(path: string): string {
  return path
    .split(".")
    .filter(Boolean)
    .map((part) =>
      part
        .split("_")
        .filter(Boolean)
        .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
        .join(" "),
    )
    .join(" > ");
}

function mapCategoryRow(
  category: TaxonomySupportedCategory,
): CategoryCoverageRow {
  return {
    id: `${category.marketplace}:${category.productType}:${category.canonicalCategoryId}`,
    marketplace: category.marketplace,
    productType: category.productType,
    sourceCategory: category.productType,
    suggestedBase: category.canonicalCategoryId
      ? formatCategoryPath(category.canonicalCategoryId)
      : "Unmapped",
    canonicalCategoryId: category.canonicalCategoryId,
    canonicalCategoryName: category.canonicalCategoryName ?? null,
    confidence: category.status === "ACTIVE" ? 1 : 0,
    status: category.status,
  };
}

export function useCompareMarketplaces() {
  const [sourceMarketplace, setSourceMarketplace] = useState("");

  const {
    data: marketplacesData,
    isLoading: marketplacesLoading,
    isError: marketplacesError,
  } = useTaxonomySupportedMarketplacesQuery();

  const marketplaces: CompareMarketplaceOption[] = useMemo(
    () =>
      (marketplacesData?.taxonomySupportedMarketplaces ?? []).map(
        (marketplace) => ({
          id: marketplace.marketplace,
          name: toMarketplaceName(marketplace.marketplace),
          categoryCount: marketplace.categoryCount,
        }),
      ),
    [marketplacesData?.taxonomySupportedMarketplaces],
  );

  useEffect(() => {
    setSourceMarketplace((current) => {
      if (
        current &&
        marketplaces.some((marketplace) => marketplace.id === current)
      ) {
        return current;
      }
      return marketplaces[0]?.id ?? "";
    });
  }, [marketplaces]);

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useTaxonomySupportedCategoriesQuery(
    { marketplace: sourceMarketplace },
    { enabled: sourceMarketplace.length > 0 },
  );

  const categoryRows = useMemo(
    () =>
      (categoriesData?.taxonomySupportedCategories ?? []).map(mapCategoryRow),
    [categoriesData?.taxonomySupportedCategories],
  );

  const summary: CompareMarketplaceSummary = useMemo(() => {
    const activeCategories = categoryRows.filter(
      (row) => row.status === "ACTIVE",
    ).length;
    const mappedFields = (
      categoriesData?.taxonomySupportedCategories ?? []
    ).reduce((total, category) => total + category.mappedFields, 0);

    return {
      supportedCategories: categoryRows.length,
      activeCategories,
      mappedFields,
      lowConfidenceItems: categoryRows.filter(
        (row) => row.confidence > 0 && row.confidence < 0.7,
      ).length,
    };
  }, [categoriesData?.taxonomySupportedCategories, categoryRows]);

  return {
    marketplaces,
    sourceMarketplace,
    setSourceMarketplace,
    categoryRows,
    summary,
    isLoading: marketplacesLoading || categoriesLoading,
    isError: marketplacesError || categoriesError,
  };
}
